import { inflate } from "pako"
import { getAppChainProvider, AppChainProvider } from "@/hooks/useChainProvider"

/**
 * Detects the data type based on chainId extracted from compressed data
 * This is more reliable than trying to decompress and check properties
 */
export type DetectionResult = {
  chainId: number | null
  dataType: AppChainProvider | null
}

export const detectDataType = (encoded: string): DetectionResult => {
  try {
    const compressed = Buffer.from(
      encoded.replace(/-/g, "+").replace(/_/g, "/"),
      "base64"
    )
    // Convert Buffer to Uint8Array and decompress
    const uint8Array = new Uint8Array(compressed)
    const decompressedData = inflate(uint8Array, { to: "string" })
    const data = JSON.parse(decompressedData)

    // Extract chainId from the data
    let chainId: number | undefined
    let dataType: AppChainProvider | null = null

    if (Array.isArray(data)) {
      // For combo bets, get chainId from first item
      const firstItem = data[0]
      chainId = firstItem?.c || firstItem?.cid || firstItem?.chainId

      // Detect data type based on data structure rather than chainId
      if (firstItem?.mh !== undefined || firstItem?.marketHash !== undefined) {
        // SX data has marketHash (mh) field
        dataType = AppChainProvider.SxBet
      } else if (
        firstItem?.g !== undefined ||
        firstItem?.gameId !== undefined
      ) {
        // Overtime data has gameId (g) field
        dataType = AppChainProvider.Overtime
      } else if (
        firstItem?.conditionId !== undefined ||
        firstItem?.cid !== undefined
      ) {
        // Azuro data has conditionId (cid) field
        dataType = AppChainProvider.Azuro
      }
    } else {
      // For single bets
      chainId = data?.c || data?.cid || data?.chainId

      // Detect data type based on data structure rather than chainId
      if (data?.mh !== undefined || data?.marketHash !== undefined) {
        // SX data has marketHash (mh) field
        dataType = AppChainProvider.SxBet
      } else if (data?.g !== undefined || data?.gameId !== undefined) {
        // Overtime data has gameId (g) field
        dataType = AppChainProvider.Overtime
      } else if (data?.conditionId !== undefined || data?.cid !== undefined) {
        // Azuro data has conditionId (cid) field
        dataType = AppChainProvider.Azuro
      }
    }

    if (!chainId || typeof chainId !== "number") {
      console.warn("No valid chainId found in compressed data")
      return { chainId: null, dataType: null }
    }

    // If we couldn't detect the data type from structure, fall back to chainId-based detection
    if (!dataType) {
      console.warn(
        "Could not detect data type from structure, falling back to chainId-based detection"
      )
      const provider = getAppChainProvider(chainId!)
      dataType = provider
    }

    console.log(`Detected data type: ${dataType} for chainId: ${chainId}`)
    return { chainId, dataType }
  } catch (error) {
    console.error("Error detecting data type:", error)
    return { chainId: null, dataType: null }
  }
}
